import {
  kPositionID,
  kParentPositionID,
  kPositionLocation,
  kPositionStartDate,
  kPositionEndDate,
  kPositionTitleID,
  kPositionDivisionID,
  kDesiredHolders,
  kStaffingTarget,
  kPositionCostBandMinimum,
  kPositionCostBandMaximum,
  kPositionBudgetedSalary,
  enumObjClassNames,
  enumObjectTypes,
  SYSTEM_CALCULATE,
  enumOverrideType
} from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { FormatFieldValue } from "@coreapp/utils/FormatFieldValue";
import {
  ProcessBasicField,
  ProcessAdvancedField,
  GetPositionSearchLabel,
  checkFieldHasMultipleValues
} from "./RecordEditor.utils";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { OverflowText, Text } from "@theorgchartdev/storybook";

/**
 * Get the chart metadata field mapping
 * @returns {Object|null} - The field mapping object or null if not available
 */
export const getChartFieldMapping = () => {
  const wsMgr = GetWSMgr();
  return wsMgr?.fDocMgr?.fChartMetadata?.fMapping || null;
};

/**
 * Check if a field is mapped to a user data source (not null, not empty, and not SYSTEM_CALCULATE)
 */
export const isUserMappedField = fieldId => {
  if (!fieldId) return false;

  const fieldMapping = getChartFieldMapping();
  if (!fieldMapping) return false;

  const fieldValue = fieldMapping[fieldId];
  return fieldValue != null && fieldValue !== "" && fieldValue !== SYSTEM_CALCULATE;
};

/**
 * Check if a field is configured (has a value length > 0 or is SYSTEM_CALCULATE)
 */
export const isFieldConfigured = fieldId => {
  if (!fieldId) return false;

  const fieldMapping = getChartFieldMapping();
  if (!fieldMapping) return false;

  const fieldValue = fieldMapping[fieldId];
  return (fieldValue != null && fieldValue !== "") || fieldValue === SYSTEM_CALCULATE;
};

/**
 * Check if a field is set to system calculate
 */
export const isFieldSystemCalculated = (manager, fieldName) => {
  if (!fieldName) return false;

  const fieldMapping = getChartFieldMapping();
  if (!fieldMapping) return false;

  return fieldMapping[fieldName] === SYSTEM_CALCULATE;
};

/**
 * Check if cost bands are "mapped" (feature enabled AND configured)
 */
export const isCostBandMapped = costBandFieldName => {
  if (!costBandFieldName) return false;

  // Get chart metadata and create PositionConfig instance
  const wsMgr = GetWSMgr();
  const positionConfigMetadata = wsMgr?.fDocMgr?.fChartMetadata?.fPositionConfig;
  if (!positionConfigMetadata) return false;

  const positionConfig = positionConfigMetadata.positionConfig;

  // First check if cost band feature is enabled
  if (!positionConfig?.useCostBand) return false;

  // Then check if the field is configured
  return isFieldConfigured(costBandFieldName);
};

/**
 * Calculate total position budget - shared logic used by both PositionEditor and BudgetDetailsDrawer
 */
export const calculateTotalPositionBudget = (
  getPositionBudgetedSalary,
  getPositionCostBandMaximum,
  getDesiredHolders
) => {
  if (!getPositionBudgetedSalary || !getPositionCostBandMaximum || !getDesiredHolders) return 0;

  const budgetSalary = getPositionBudgetedSalary() || 0;
  if (budgetSalary === 0) {
    return (getPositionCostBandMaximum() || 0) * (getDesiredHolders() || 1);
  }
  return budgetSalary;
};

/**
 * Get dynamic tooltip text for position budgeted salary field
 * @param {Object} position - The position object
 * @returns {string} The tooltip text
 */
export const getPositionBudgetTooltip = position => {
  const isPositionBudgetMapped = isUserMappedField(kPositionBudgetedSalary);
  const isCostBandsMapped = isCostBandMapped(kPositionCostBandMaximum);

  if (!isPositionBudgetMapped && isCostBandsMapped) {
    // Scenario 4: Cost bands ARE mapped and position budget is NOT mapped
    const costBandMaxFieldDef = position?.GetFieldDefByID(kPositionCostBandMaximum);
    const costBandMaxFieldName = costBandMaxFieldDef?.GetFieldName() || kPositionCostBandMaximum;
    const desiredHoldersFieldDef = position?.GetFieldDefByID(kDesiredHolders);
    const desiredHoldersFieldName = desiredHoldersFieldDef?.GetFieldName() || kStaffingTarget;

    return appstr("Cost band max staffing target position budget", {
      costBandMax: costBandMaxFieldName,
      desiredHolders: desiredHoldersFieldName
    });
  } else {
    const positionBudgetFieldDef = position?.GetFieldDefByID(kPositionBudgetedSalary);
    const fieldName = positionBudgetFieldDef?.GetFieldName() || kPositionBudgetedSalary;
    return appstr("This is driven by the position budget field", { field: fieldName });
  }
};

/**
 * Maps error messages to field names for proper error detection
 * Handles both server-side field names and client-side error messages
 * @param {string|Array} error - Error message or field name
 * @param {string} fieldName - The field name to check against
 * @returns {boolean} - True if the error applies to this field
 */
export const doesErrorApplyToField = (error, fieldName) => {
  if (!error || !fieldName) return false;

  // If error is already the field name, direct match
  if (error === fieldName) return true;

  // Map common error message patterns to field names
  const errorMappings = {
    // Position Title errors
    "Position Title": ["Position Title is required", "Title"],
    Title: ["Position Title is required", "Position Title"],

    // Parent Position errors
    "Parent PositionID": ["Parent PositionID is required", "Reports to"],
    "Reports to": ["Parent PositionID is required", "Parent PositionID"],

    // Desired Holders / Staffing Target errors
    "Desired Holders": ["Allocation must be a positive number", "staffingtargeterror"],
    Allocation: ["Desired Holders"],

    // Cost Band errors
    "Position Cost Band Minimum": ["Cost Band Minimum must be a number", "costbanderror"],
    "Position Cost Band Maximum": ["Cost Band Maximum must be a number", "costbanderror"],
    "Cost Band Minimum": ["Position Cost Band Minimum"],
    "Cost Band Maximum": ["Position Cost Band Maximum"],

    // Date errors
    "Position Start Date": ["End Date must be on or after Start Date", "startenddateerror"],
    "Position End Date": ["End Date must be on or after Start Date", "startenddateerror"],
    "Start Date": ["Position Start Date"],
    "End Date": ["Position End Date"],

    // Location and Division
    "Position Location": ["Location"],
    "Position Division": ["Division"],
    Location: ["Position Location"],
    Division: ["Position Division"]
  };

  // Check if fieldName maps to any error patterns
  const fieldMappings = errorMappings[fieldName] || [];
  if (fieldMappings.some(pattern => typeof error === "string" && error.includes(pattern))) {
    return true;
  }

  // Check if error maps to this fieldName
  for (const [mappedField, patterns] of Object.entries(errorMappings)) {
    if (
      mappedField === fieldName &&
      patterns.some(pattern => typeof error === "string" && error.includes(pattern))
    ) {
      return true;
    }
  }

  // Fallback: check if error message contains the field name
  if (typeof error === "string" && fieldName) {
    return error.toLowerCase().includes(fieldName.toLowerCase());
  }

  return false;
};

// Required fields for position editor validation
export const requiredPositionFields = [
  kPositionID,
  kParentPositionID,
  kPositionLocation,
  kPositionTitleID
];

// Field configuration for position
export const GetPositionFieldsConfig = () => [
  {
    id: kPositionTitleID,
    textEditOptions: {
      row: 1,
      order: 1,
      showTrigger: false,
      inputProps: { require: true }
    }
  },
  {
    id: kPositionID,
    textEditOptions: {
      row: 1,
      order: 2,
      showTrigger: false,
      inputProps: { disabled: true }
    }
  },
  {
    id: kParentPositionID,
    textEditOptions: {
      row: 2,
      order: 1,
      isSupervisorField: true,
      showTrigger: false,
      inputProps: { require: true },
      width: "80%"
    }
  },
  {
    id: kDesiredHolders,
    textEditOptions: {
      row: 2,
      order: 2,
      showTrigger: false,
      width: "20%",
      inputProps: {
        labelInfo: appstr("staffingtargetinfo")
      }
    }
  },
  {
    id: kPositionDivisionID,
    textEditOptions: {
      row: 3,
      order: 1,
      showTrigger: true
    }
  },
  {
    id: kPositionLocation,
    textEditOptions: {
      row: 3,
      order: 2,
      showTrigger: true
    }
  },
  {
    id: kPositionStartDate,
    textEditOptions: {
      row: 4,
      order: 1,
      showTrigger: false
    }
  },
  {
    id: kPositionEndDate,
    textEditOptions: {
      row: 4,
      order: 2,
      showTrigger: false
    }
  },
  {
    id: kPositionBudgetedSalary,
    textEditOptions: {
      row: 5,
      order: 1,
      showTrigger: false,
      width: "100%"
    }
  }
];

// Range fields configuration for cost band
export const rangeFields = [{ costband: [kPositionCostBandMinimum, kPositionCostBandMaximum] }];

/**
 * Build supervisor field props for parent position ID field
 * @param {Object} values - Field values
 * @param {Object} recordSupervisor - Current supervisor record
 * @param {Object} parentPosition - Parent position for create mode
 * @param {Function} onRemoveParentPosition - Callback for removing parent position
 * @param {boolean} isCreateMode - Whether in create mode
 * @param {boolean} hasMultipleValues - Whether multiple values exist (multi-edit mode)
 * @param {Object} curChanges - Current changes object
 * @param {string} fieldName - Field name
 * @param {boolean} isEditable - Whether the field should be editable (respects field-level + conflict override)
 * @param {Object} doc - Document object
 * @param {string|null} queryChartId - Chart ID for DB search queries (used with append tables)
 * @returns {Object} Supervisor field props configuration
 */
const buildSupervisorFieldProps = (
  values,
  recordSupervisor,
  parentPosition,
  onRemoveParentPosition,
  isCreateMode,
  hasMultipleValues = false,
  curChanges = null,
  fieldName = null,
  isEditable = true,
  doc = null,
  queryChartId = null
) => {
  return {
    labelWrapperProps: {
      label: appstr("Reports to"),
      require: true
    },
    selectableSearchMenuProps: {
      disabled: !isEditable,
      inputSize: "medium",
      pillSize: "medium",
      displayRecents: true,
      expandable: false,
      allowMultiSelect: false,
      listWidth: "fill",
      fullWidth: true,
      queryChartId,
      selectedItem: (() => {
        const isValidSupervisorId = id => {
          return id && id !== -1 && id !== "-1" && id !== undefined && id !== null;
        };

        // Multi-edit mode with changes
        if (hasMultipleValues && fieldName in curChanges) {
          const supervisorId = recordSupervisor?.SmartGetFieldValue("PositionID");
          if (!isValidSupervisorId(supervisorId)) {
            return undefined;
          }
          return {
            label: GetPositionSearchLabel(recordSupervisor),
            value: supervisorId
          };
        }

        // Multi-edit mode without changes
        if (hasMultipleValues) {
          return undefined;
        }

        // Create mode logic
        if (isCreateMode) {
          const parentPosId = values[kParentPositionID];
          if (!isValidSupervisorId(parentPosId)) {
            return undefined;
          }
          // Use recordSupervisor if available (when user has selected a different supervisor)
          // Otherwise use the initial parentPosition
          const supervisorToShow = recordSupervisor || parentPosition;
          if (!supervisorToShow) {
            return undefined;
          }
          return {
            label: GetPositionSearchLabel(supervisorToShow),
            value: parentPosId
          };
        }

        // Edit mode logic
        if (!recordSupervisor) {
          return undefined;
        }
        const supervisorId = recordSupervisor.SmartGetFieldValue("PositionID");
        if (!isValidSupervisorId(supervisorId)) {
          return undefined;
        }
        return {
          label: GetPositionSearchLabel(recordSupervisor),
          value: supervisorId
        };
      })(),
      onRemoveItem: isEditable ? onRemoveParentPosition : undefined,
      searchMenuProps: {
        clearOnSelect: true,
        clearable: true,
        placeholder:
          hasMultipleValues && !curChanges[fieldName]
            ? `(${appstr("multiple values")})`
            : appstr("Search for a position"),
        expandWidth: true,
        inputProps: {
          disabled: !isEditable
        }
      },
      queryOptions: {
        includeAux: false,
        queryType: "position"
      },
      handlePositions: true,
      mockDoc: doc
    }
  };
};

const processMultiEditFieldValues = ({
  multiDataObjs,
  item,
  fieldID,
  fieldName,
  record,
  getCurrentFieldValue,
  curChanges,
  dbObj
}) => {
  const wsMgr = GetWSMgr();
  const planManager = wsMgr?.fPlanManager;

  const allValuesWithPositions = multiDataObjs.map(obj => {
    let value = "";
    const fieldDef = obj.GetFieldDefByID(fieldID);
    if (fieldDef) {
      const overrideObj = planManager?.GetActiveOverrideObj(obj);
      const lastChangedField = planManager?.lastActiveChangedField(overrideObj, fieldName);
      let activeValue = null;
      if (lastChangedField !== null) {
        activeValue = lastChangedField.fDelta[fieldName];
      }
      const rawValue = obj.GetFieldValue(fieldDef, false);

      value = activeValue !== null ? String(activeValue) : rawValue != null ? String(rawValue) : "";
    }
    return { position: obj, value };
  });

  const allValues = allValuesWithPositions.map(item => item.value);
  const allSame = allValues.every(val => val === allValues[0]);

  let fieldValue;
  let multiValueIndicator = null;
  let individualValues = null;
  let valuesList = [];

  if (allSame) {
    fieldValue = item.textEditOptions?.isSupervisorField
      ? GetPositionSearchLabel(record)
      : getCurrentFieldValue(fieldName) || "";
  } else {
    if (item.textEditOptions?.isSupervisorField) {
      fieldValue = fieldName in curChanges ? GetPositionSearchLabel(record) : "";
    } else {
      fieldValue = fieldName in curChanges ? curChanges[fieldName] : "";
    }
    multiValueIndicator = `(${appstr("multiple values")})`;

    const currentFieldDef = dbObj.GetParentClass().GetFieldDefByName(fieldName);
    const positionsByValue = {};

    allValuesWithPositions.forEach(item => {
      const formattedValue =
        item.value !== "" && currentFieldDef ? FormatFieldValue(item.value, currentFieldDef) : "";

      if (!positionsByValue[formattedValue]) {
        positionsByValue[formattedValue] = [];
      }
      positionsByValue[formattedValue].push(item.position);
    });

    Object.entries(positionsByValue).forEach(([value, positions]) => {
      valuesList.push({
        value: value === "" ? `(${appstr("Blank").toLowerCase()})` : value,
        positions
      });
    });

    individualValues =
      valuesList.length > 0
        ? `${appstr("Values")}: ${valuesList.map(item => item.value).join(", ")}`
        : null;
  }

  return {
    fieldValue,
    multiValueIndicator,
    individualValues,
    valuesList
  };
};

/**
 * Build the field map for BasicInfoSection in Position related modals
 *
 * @param {Object} options Options for building the field map
 * @param {Object} options.values Values for the fields
 * @param {Array} options.errors Errors for the fields
 * @param {Object} options.dbObj The database object (for editing)
 * @param {Object} options.workingOverride The working override (for editing)
 * @param {string} options.changeType The change type (for editing)
 * @param {Object} options.manager The manager (for editing)
 * @param {Object} options.record The record (for editing)
 * @param {Function} options.getCurrentFieldValue Function to get current field value (for editing)
 * @param {boolean} options.isCreateMode Whether we're in create mode (not editing)
 * @param {Function} options.GetExistingFieldChanges Function to get existing field changes (for edit mode)
 * @param {Function} options.unsavedFieldChange Function to check unsaved field changes (for edit mode)
 * @param {boolean} options.isEditable Whether all fields should be editable (top-level override for conflicts)
 * @returns {Object} The field map for BasicInfoSection
 */
export const BuildPositionFieldMap = options => {
  const {
    values,
    errors = [],
    dbObj,
    workingOverride,
    changeType,
    manager,
    record,
    getCurrentFieldValue,
    isCreateMode = false,
    onRemoveParentPosition,
    recordSupervisor,
    parentPosition,
    GetExistingFieldChanges,
    unsavedFieldChange,
    isMultiEdit = false,
    multiDataObjs = null,
    curChanges = {},
    GetRevertButton,
    isEditable = true,
    queryChartId = null
  } = options;

  const doc = manager?.GetDoc?.() || GetDoc();
  const fieldMap = {};
  const positionFieldsConfig = GetPositionFieldsConfig();
  const objDB = doc?.GetObjDB();
  const positionClass = objDB.GetClassByName(enumObjClassNames.position);

  // For create mode, we don't need all these checks
  if (isCreateMode) {
    positionFieldsConfig.forEach(item => {
      const fieldID = item.id;
      const fieldDef = positionClass.GetFieldDefByID(fieldID);
      if (!fieldDef) return;
      const fieldName = fieldDef.GetFieldName();

      // Process the field options - for create mode, we always enable editing except for Position ID
      const fieldTextEditOptions = {
        enableTE: true,
        ...item.textEditOptions
      };

      if (fieldID === kPositionID) {
        fieldTextEditOptions.enableTE = true;
        fieldTextEditOptions.inputProps = { disabled: false, require: true };
      }
      if (fieldID === kParentPositionID) {
        fieldTextEditOptions.isSupervisorField = true;
        fieldTextEditOptions.supervisorFieldProps = buildSupervisorFieldProps(
          values,
          recordSupervisor,
          parentPosition,
          onRemoveParentPosition,
          isCreateMode,
          false,
          curChanges,
          fieldName,
          isEditable,
          doc,
          queryChartId
        );
      }
      fieldMap[fieldID] = {
        name: fieldName,
        value: values[fieldID] || "",
        textEditOptions: fieldTextEditOptions,
        error: errors.some(error => doesErrorApplyToField(error, fieldName)),
        fieldDef: fieldDef || {
          GetFieldName: () => fieldName
        }
      };
    });

    return fieldMap;
  }

  // For edit mode, we need to do more processing
  // Return empty object if record is not available yet
  if (!record || !dbObj || !workingOverride) {
    return {};
  }

  positionFieldsConfig.forEach(item => {
    const fieldID = item.id;
    const fieldDef = dbObj.GetFieldDefByID(fieldID);

    if (!fieldDef) {
      return;
    }

    const fieldName = fieldDef.GetFieldName();
    if (!fieldName) {
      return;
    }

    // Process the field options based on whether it's a required field
    const fieldTextEditOptions = requiredPositionFields.includes(fieldID)
      ? ProcessBasicField(
          workingOverride,
          fieldID,
          changeType,
          manager.GetManagerName(),
          false,
          undefined
        )
      : ProcessAdvancedField(fieldDef, changeType, false, undefined);

    // Get field value, checking for supervisor field or regular field
    let fieldValue;
    let multiValueIndicator = null;
    let individualValues = null;
    let valuesList = [];

    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      const multiEditResult = processMultiEditFieldValues({
        multiDataObjs,
        item,
        fieldID,
        fieldName,
        record,
        getCurrentFieldValue,
        curChanges,
        dbObj
      });

      fieldValue = multiEditResult.fieldValue;
      multiValueIndicator = multiEditResult.multiValueIndicator;
      individualValues = multiEditResult.individualValues;
      valuesList = multiEditResult.valuesList;
    } else {
      if (item.textEditOptions?.isSupervisorField) {
        fieldValue = GetPositionSearchLabel(record);
      } else {
        fieldValue = getCurrentFieldValue(fieldName) || "";
      }
    }

    const hasError = fieldTextEditOptions.isSupervisorField
      ? errors.some(error => doesErrorApplyToField(error, "Reports to"))
      : errors.some(error => doesErrorApplyToField(error, fieldName));
    let fieldChanged = false;
    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      // Multi-edit mode: only check unsaved field changes
      if (unsavedFieldChange) {
        fieldChanged = unsavedFieldChange(fieldName);
      }
    } else {
      // Single edit mode: check both existing changes and unsaved changes
      if (GetExistingFieldChanges && unsavedFieldChange) {
        const hasExistingChanges = GetExistingFieldChanges(fieldName)?.length > 0;
        const hasUnsavedChanges = unsavedFieldChange(fieldName);
        fieldChanged = hasExistingChanges || hasUnsavedChanges;
      }
    }

    let finalTextEditOptions = {
      ...(fieldTextEditOptions === false ? { enableTE: false } : fieldTextEditOptions),
      ...item.textEditOptions
    };

    if (finalTextEditOptions.enableTE !== undefined) {
      finalTextEditOptions.enableTE = finalTextEditOptions.enableTE && isEditable;
    }
    if (finalTextEditOptions.showTrigger !== undefined) {
      finalTextEditOptions.showTrigger = finalTextEditOptions.showTrigger && isEditable;
    }

    if (fieldID === kPositionBudgetedSalary) {
      const isPositionBudgetMapped = isUserMappedField(kPositionBudgetedSalary);
      const isDeletion = changeType === enumOverrideType.delete;

      if (isPositionBudgetMapped) {
        // Position budget is user-mapped - always editable as long as it's not a delete operation (Scenarios 2 & 3)
        finalTextEditOptions.enableTE = !isDeletion;
        finalTextEditOptions.inputProps = {
          ...finalTextEditOptions.inputProps,
          disabled: isDeletion
        };
      } else {
        const isCostBandsMapped = isCostBandMapped(kPositionCostBandMaximum);

        if (isCostBandsMapped) {
          // Scenario 4: Cost bands ARE mapped and position budget is NOT mapped - disabled
          // Only show labelInfo when field is calculated
          const labelInfo = getPositionBudgetTooltip(dbObj);
          finalTextEditOptions.enableTE = false;
          finalTextEditOptions.inputProps = {
            ...finalTextEditOptions.inputProps,
            disabled: true,
            labelInfo: labelInfo
          };
        } else {
          // Scenario 1: Neither cost bands nor position budget mapped - editable
          finalTextEditOptions.enableTE = !isDeletion;
          finalTextEditOptions.inputProps = {
            ...finalTextEditOptions.inputProps,
            disabled: isDeletion
          };
        }
      }
    }

    if (fieldID === kParentPositionID && onRemoveParentPosition) {
      const hasMultipleValues =
        isMultiEdit &&
        multiDataObjs &&
        multiDataObjs.length > 1 &&
        checkFieldHasMultipleValues(fieldName, multiDataObjs);

      const supervisorFieldEditable = finalTextEditOptions.enableTE !== false && isEditable;
      finalTextEditOptions.supervisorFieldProps = buildSupervisorFieldProps(
        values,
        recordSupervisor,
        parentPosition,
        onRemoveParentPosition,
        isCreateMode,
        hasMultipleValues,
        curChanges,
        fieldName,
        supervisorFieldEditable,
        doc,
        queryChartId
      );
    }

    const valuesListTooltip =
      valuesList && valuesList.length > 0 ? (
        <div>
          <OverflowText
            size="small"
            style={{ marginBottom: "4px" }}
            colorModifier={"white"}
            modifier={"medium"}
          >
            {appstr("Current values")}:
          </OverflowText>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {valuesList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: "8px" }}>
                <OverflowText size="xsmall" modifier="medium" colorModifier={"white"}>
                  {item.value}
                </OverflowText>
                <div style={{ marginLeft: "8px", marginTop: "2px" }}>
                  {item.positions.map((pos, posIdx) => (
                    <OverflowText key={posIdx} size="xsmall" colorModifier="faintgrey">
                      {FormatPositionLabel(pos)}
                    </OverflowText>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null;

    fieldMap[fieldID] = {
      name: fieldName,
      value: fieldValue,
      textEditOptions: {
        ...finalTextEditOptions,
        ...(multiValueIndicator && {
          inputProps: {
            ...finalTextEditOptions.inputProps,
            placeholder: multiValueIndicator
          }
        })
      },
      error: hasError,
      warning: fieldChanged,
      fieldDef: fieldDef,
      multiValueIndicator,
      revertButton: GetRevertButton && isEditable ? GetRevertButton(fieldDef) : null,
      individualValues,
      valuesListTooltip
    };
  });

  return fieldMap;
};

/**
 * Build the range fields map for BasicInfoSection in Position related modals
 *
 * @param {Object} options Options for building the range fields map
 * @param {Object} options.values Values for the fields
 * @param {Object} options.dbObj The database object (for editing)
 * @param {Object} options.workingOverride The working override (for editing)
 * @param {string} options.changeType The change type (for editing)
 * @param {Object} options.record The record (for editing)
 * @param {Function} options.getCurrentFieldValue Function to get current field value (for editing)
 * @param {boolean} options.isCreateMode Whether we're in create mode (not editing)
 * @param {Function} options.GetExistingFieldChanges Function to get existing field changes (for edit mode)
 * @param {Function} options.unsavedFieldChange Function to check unsaved field changes (for edit mode)
 * @returns {Object} The range fields map for BasicInfoSection
 */
export const BuildPositionRangeFieldsMap = options => {
  const {
    values,
    errors = [],
    dbObj,
    workingOverride,
    changeType,
    record,
    getCurrentFieldValue,
    isCreateMode = false,
    GetExistingFieldChanges,
    unsavedFieldChange,
    GetRevertButton,
    isMultiEdit,
    multiDataObjs,
    curChanges,
    isEditable = true,
    manager
  } = options;

  const rangedMap = {};
  const doc = manager?.GetDoc?.() || GetDoc();
  const objDB = doc.GetObjDB();
  const positionClass = objDB.GetClassByName(enumObjClassNames.position);

  // For create mode, build a simpler map
  if (isCreateMode) {
    rangeFields.forEach(item => {
      const key = Object.keys(item)[0];
      const minFieldId = item[key][0];
      const maxFieldId = item[key][1];
      const minFieldDef = positionClass.GetFieldDefByID(minFieldId);
      const maxFieldDef = positionClass.GetFieldDefByID(maxFieldId);

      rangedMap[key] = {
        min: {
          name: minFieldId,
          value: values[minFieldId] || "",
          textEditOptions: {
            enableTE: true,
            inputProps: { allowNegative: false }
          },
          error: errors.some(error =>
            doesErrorApplyToField(error, minFieldDef?.GetFieldName() || minFieldId)
          ),
          fieldDef: minFieldDef || {
            GetFieldName: () => minFieldId
          }
        },
        max: {
          name: maxFieldId,
          value: values[maxFieldId] || "",
          textEditOptions: {
            enableTE: true,
            inputProps: { allowNegative: false }
          },
          error: errors.some(error =>
            doesErrorApplyToField(error, maxFieldDef?.GetFieldName() || maxFieldId)
          ),
          fieldDef: maxFieldDef || {
            GetFieldName: () => maxFieldId
          }
        }
      };
    });

    return rangedMap;
  }

  // For edit mode, we need the record and other data
  // Return empty object if record is not available yet
  if (!record || !dbObj || !workingOverride) {
    return {};
  }

  rangeFields.forEach(item => {
    const key = Object.keys(item)[0];
    const minFieldDef = dbObj.GetFieldDefByID(item[key][0]);
    const maxFieldDef = dbObj.GetFieldDefByID(item[key][1]);

    // Skip if either field definition doesn't exist
    if (!minFieldDef || !maxFieldDef) return;

    const minFieldName = minFieldDef.GetFieldName();
    const maxFieldName = maxFieldDef.GetFieldName();

    // Skip if either field name doesn't exist
    if (!minFieldName || !maxFieldName) return;

    // Check if these fields have been changed (for warning status) - mirror BuildPositionFieldMap logic
    let minFieldChanged = false;
    let maxFieldChanged = false;

    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      if (unsavedFieldChange) {
        minFieldChanged = unsavedFieldChange(minFieldName);
        maxFieldChanged = unsavedFieldChange(maxFieldName);
      }
    } else {
      if (GetExistingFieldChanges && unsavedFieldChange) {
        const hasMinExistingChanges = GetExistingFieldChanges(minFieldName)?.length > 0;
        const hasMinUnsavedChanges = unsavedFieldChange(minFieldName);
        minFieldChanged = hasMinExistingChanges || hasMinUnsavedChanges;

        const hasMaxExistingChanges = GetExistingFieldChanges(maxFieldName)?.length > 0;
        const hasMaxUnsavedChanges = unsavedFieldChange(maxFieldName);
        maxFieldChanged = hasMaxExistingChanges || hasMaxUnsavedChanges;
      }
    }

    let minFieldValue;
    let minMultiValueIndicator = null;
    let minIndividualValues = null;
    let minValuesList = [];

    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      const minMultiEditResult = processMultiEditFieldValues({
        multiDataObjs,
        item: { fieldID: item[key][0], fieldName: minFieldName },
        fieldID: item[key][0],
        fieldName: minFieldName,
        record,
        getCurrentFieldValue,
        curChanges,
        dbObj
      });

      minFieldValue = minMultiEditResult.fieldValue;
      minMultiValueIndicator = minMultiEditResult.multiValueIndicator;
      minIndividualValues = minMultiEditResult.individualValues;
      minValuesList = minMultiEditResult.valuesList;
    } else {
      minFieldValue = getCurrentFieldValue(minFieldName) || "";
    }

    let maxFieldValue;
    let maxMultiValueIndicator = null;
    let maxIndividualValues = null;
    let maxValuesList = [];

    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      const maxMultiEditResult = processMultiEditFieldValues({
        multiDataObjs,
        item: { fieldID: item[key][1], fieldName: maxFieldName },
        fieldID: item[key][1],
        fieldName: maxFieldName,
        record,
        getCurrentFieldValue,
        curChanges,
        dbObj
      });

      maxFieldValue = maxMultiEditResult.fieldValue;
      maxMultiValueIndicator = maxMultiEditResult.multiValueIndicator;
      maxIndividualValues = maxMultiEditResult.individualValues;
      maxValuesList = maxMultiEditResult.valuesList;
    } else {
      maxFieldValue = getCurrentFieldValue(maxFieldName) || "";
    }

    const minValuesListTooltip =
      minValuesList && minValuesList.length > 0 ? (
        <div>
          <OverflowText
            size="small"
            style={{ marginBottom: "4px" }}
            colorModifier={"white"}
            modifier={"medium"}
          >
            {appstr("Current values")}:
          </OverflowText>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {minValuesList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: "8px" }}>
                <OverflowText size="xsmall" modifier="medium" colorModifier={"white"}>
                  {item.value}
                </OverflowText>
                <div style={{ marginLeft: "8px", marginTop: "2px" }}>
                  {item.positions.map((pos, posIdx) => (
                    <OverflowText key={posIdx} size="xsmall" colorModifier="faintgrey">
                      {FormatPositionLabel(pos)}
                    </OverflowText>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null;

    const maxValuesListTooltip =
      maxValuesList && maxValuesList.length > 0 ? (
        <div>
          <OverflowText
            size="small"
            style={{ marginBottom: "4px" }}
            colorModifier={"white"}
            modifier={"medium"}
          >
            {appstr("Current values")}:
          </OverflowText>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {maxValuesList.map((item, idx) => (
              <li key={idx} style={{ marginBottom: "8px" }}>
                <OverflowText size="xsmall" modifier="medium" colorModifier={"white"}>
                  {item.value}
                </OverflowText>
                <div style={{ marginLeft: "8px", marginTop: "2px" }}>
                  {item.positions.map((pos, posIdx) => (
                    <OverflowText key={posIdx} size="xsmall" colorModifier="faintgrey">
                      {FormatPositionLabel(pos)}
                    </OverflowText>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null;

    const minFieldTextEdit = ProcessAdvancedField(minFieldDef, changeType, false, undefined);
    const maxFieldTextEdit = ProcessAdvancedField(maxFieldDef, changeType, false, undefined);

    rangedMap[key] = {
      min: {
        name: minFieldName,
        value: minFieldValue,
        textEditOptions: {
          ...minFieldTextEdit,
          enableTE: minFieldTextEdit.enableTE && isEditable,
          inputProps: {
            allowNegative: false,
            tooltip: appstr("costbandinfo"),
            ...(minMultiValueIndicator && {
              placeholder: minMultiValueIndicator
            })
          }
        },
        warning: minFieldChanged,
        error: errors.some(error => doesErrorApplyToField(error, minFieldName)),
        fieldDef: minFieldDef,
        revertButton: GetRevertButton && isEditable ? GetRevertButton(minFieldDef) : null,
        multiValueIndicator: minMultiValueIndicator,
        individualValues: minIndividualValues,
        valuesListTooltip: minValuesListTooltip
      },
      max: {
        name: maxFieldName,
        value: maxFieldValue,
        textEditOptions: {
          ...maxFieldTextEdit,
          enableTE: maxFieldTextEdit.enableTE && isEditable,
          inputProps: {
            allowNegative: false,
            tooltip: appstr("costbandinfo"),
            ...(maxMultiValueIndicator && {
              placeholder: maxMultiValueIndicator
            })
          }
        },
        warning: maxFieldChanged,
        error: errors.some(error => doesErrorApplyToField(error, maxFieldName)),
        fieldDef: maxFieldDef,
        revertButton: GetRevertButton && isEditable ? GetRevertButton(maxFieldDef) : null,
        multiValueIndicator: maxMultiValueIndicator,
        individualValues: maxIndividualValues,
        valuesListTooltip: maxValuesListTooltip
      }
    };
  });

  return rangedMap;
};

/**
 * Helper function to find an object by ID
 *
 * @param {string} id
 * @param {string=} className
 * @param {Object=} doc
 */
export const FindObject = (id, className = enumObjClassNames.box, doc = null) => {
  if (!id) return null;

  const docToUse = doc || GetDoc();
  const objDB = docToUse.GetObjDB();
  const boxClass = objDB.GetClassByName(className);
  const personIDDef = boxClass.GetRecordIDFieldDef(boxClass);
  return boxClass.FindObject(personIDDef, id, true);
};

/**
 * Create a cloned position override from a parent position
t * @param {string} type - Override type (add, change, etc.)
 * @param {object} manager - Plan manager or table manager (optional, will fallback to planMgr from WSMgr)
 */
export const createPositionOverride = (type, manager = null) => {
  const wsMgr = GetWSMgr();

  const mgr = manager || wsMgr?.GetPlanMgr();
  const overrideClass = mgr?.fOverrideClass;

  if (!wsMgr || !mgr || !overrideClass) return null;

  // Create a new override
  const positionOverride = new QObjDBObject();
  positionOverride.InitObject(overrideClass);
  positionOverride.SmartSetFieldValue("Type", type);
  positionOverride.SmartSetFieldValue("objType", enumObjectTypes.position);

  return positionOverride;
};

/**
 * Load a position by ID, checking cache first then loading from server if needed
 * @param {string} positionID - The position ID to load
 * @param {Function} callback - Callback function called with the loaded position
 * @param {boolean} outsidePlan - Whether to look for positions outside the current plan
 */
export const loadPositionById = (positionID, callback, outsidePlan = false) => {
  if (!positionID) {
    callback(null);
    return;
  }

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();

  if (!planMgr) {
    callback(null);
    return;
  }

  // First try to find the position locally
  let position = FindObject(positionID, enumObjClassNames.position);

  if (!position) {
    // Try to get from cache
    position = planMgr.GetCacheBaseRecord(enumObjectTypes.position, positionID);
  }

  if (position) {
    callback(position);
    return;
  }

  planMgr.LoadRecords(
    {
      positionIDs: [positionID],
      outsidePlan,
      includeHolders: true
    },
    response => {
      const loadedPosition = planMgr.GetCacheBaseRecord(enumObjectTypes.position, positionID);
      callback(loadedPosition);
    }
  );
};

/**
 * Load an employee by ID, checking cache first then loading from server if needed
 * @param {string} employeeID - The employee ID to load
 * @param {Function} callback - Callback function called with the loaded employee
 * @param {boolean} outsidePlan - Whether to look for employees outside the current plan
 */
export const loadEmployeeById = (employeeID, callback, outsidePlan = false) => {
  if (!employeeID) {
    callback(null);
    return;
  }

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();

  if (!planMgr) {
    callback(null);
    return;
  }

  // First try to find the employee locally
  let employee = FindObject(employeeID, enumObjClassNames.box);

  if (!employee) {
    // Try to get from cache
    employee = planMgr.GetCacheBaseRecord(enumObjectTypes.holder, employeeID);
  }

  if (employee) {
    callback(employee);
    return;
  }

  // Load the employee from server
  planMgr.LoadRecords(
    {
      recordIDs: [employeeID],
      outsidePlan
    },
    response => {
      const loadedEmployee = planMgr.GetCacheBaseRecord(enumObjectTypes.holder, employeeID);
      callback(loadedEmployee);
    }
  );
};
